import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';

import constMeta from '@/contents/constants/constMeta';
import Button from '@/contents/General/Button';
import Image from '@/contents/General/Image';
import { Thumbnail } from '@/contents/General/Panels';
import Stack from '@/contents/General/Stack';
import Typography from '@/contents/General/Typography';
import { GlobalContext } from '@/contents/hooks/state';
import Layout from '@/layouts';
import { createClient } from '@/prismicio';
import theme from '@/styles/theme';
import type { NextPageWithLayout } from '@/types';
import { Page } from '@/types/domain';
import type { PostDocument } from '@/types/prismic.generated';
import getRevalidateSec from '@/utils/getRevalidateSec';

type Props = {
  posts: PostDocument[];
};
const Container = styled(Box)`
  position: relative;
  padding: 0 ${theme.spacing(3)};
`;

const Index: NextPageWithLayout<Props> = ({ posts }) => {
  const router = useRouter();
  const { setPage } = useContext(GlobalContext);

  useEffect(() => {
    setPage(Page.MAGAZINE);
  }, []);

  const nextPage = (path: string) => {
    router.push(`/magazine/${path}`);
  };

  return (
    <>
      <Head>
        <title>{constMeta.magazine.title}</title>
        <meta
          name="description"
          content={constMeta.magazine.mata_description}
        />
        <meta property="og:url" content="https://iihi.life/magazine" />
        <meta property="og:title" content={constMeta.magazine.title} />
        <meta
          property="og:description"
          content={constMeta.magazine.mata_description}
        />
        <meta
          property="og:image"
          content="https://iihi.life/assets/images/common/ogp.jpg"
        />
      </Head>
      <Container>
        <Box paddingTop={5} paddingBottom={6}>
          <Typography
            fontSize={28}
            fontWeight={700}
            sx={{ paddingBottom: theme.spacing(2) }}
            variant="h1"
          >
            iihi Magazine「火種」
          </Typography>
          <Typography fontSize={15} paddingBottom={2}>
            キャンプ場を探す時や魅力的なキャンプ場を見つけた時の高揚感、好奇心・探究心がうずく瞬間。それは人を動かす熱源です。
          </Typography>
          <Typography fontSize={15} paddingBottom={2}>
            さまざまなプレイヤーにその熱源を尋ねるインタビュー企画「火種」では、何を軸・支えに、何を動力として、どうその活動を展開しているのかという、その人の力学をひもときます。
          </Typography>
          <Typography fontSize={15}>
            誰かの火種が、次の誰かの熱源に。そんな連鎖を生むことを願って。
          </Typography>
        </Box>
        <Stack column space={5}>
          {posts.map((post) => (
            <Button
              key={post.uid}
              onClick={() => nextPage(post.uid)}
              component="label"
            >
              <Stack column space={2} width="100%">
                {post.data.topImage.url && (
                  <Thumbnail image_height={50} max_height={256}>
                    <Image
                      src={post.data.topImage.url?.toString()}
                      width="100%"
                      alt={post.data.title ?? ''}
                    />
                  </Thumbnail>
                )}
                <Typography fontSize={14} fontWeight={500} variant="h2">
                  {post.data.title}
                </Typography>
              </Stack>
            </Button>
          ))}
        </Stack>
      </Container>
    </>
  );
};

Index.getLayout = (page) => <Layout>{page}</Layout>;

export default Index;

export async function getStaticProps({ previewData }: any) {
  const client = createClient({ previewData });

  const posts = await client.getAllByType('post', {
    orderings: {
      field: 'document.first_publication_date',
      direction: 'desc',
    },
    limit: 100,
  });

  return { props: { posts }, revalidate: getRevalidateSec('long') };
}
